import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Link as LinkIcon, FileText, Upload, Download, Code, MessageSquare, Globe, Edit, Mail, User, Bell, Webhook, Archive, ArchiveRestore, BarChart3, Settings as SettingsIcon, ChevronDown, ChevronUp, Network, GripVertical, X, RefreshCw, Star } from 'lucide-react';
import AnalyticsDashboard from './AnalyticsDashboard';
import ScrapeTimeline from '@/components/ScrapeTimeline';
import CrawlProgressBar from '@/components/CrawlProgressBar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientManagement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [knowledge, setKnowledge] = useState([]);
  const [starterQuestions, setStarterQuestions] = useState([]);  // Array of {question, answer, resource_urls}
  const [draggedQuestionIndex, setDraggedQuestionIndex] = useState(null);
  const [editingAnswer, setEditingAnswer] = useState(null);  // {index, question, answer, resource_urls}
  const [suggestedAnswer, setSuggestedAnswer] = useState("");
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [regeneratingEmbeddings, setRegeneratingEmbeddings] = useState(false);
  const [regeneratingKBEmbeddings, setRegeneratingKBEmbeddings] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [newText, setNewText] = useState({ title: '', content: '' });
  const [newUrl, setNewUrl] = useState('');
  const [siteUrl, setSiteUrl] = useState(''); // New: for full site content extraction
  const [pastedUrls, setPastedUrls] = useState(''); // New: for bulk URL paste
  const [isProcessingUrls, setIsProcessingUrls] = useState(false); // Track processing state
  const [isCrawlingSite, setIsCrawlingSite] = useState(false); // Track site crawl initiation
  const [pdfFile, setPdfFile] = useState(null);
  const [newQuestion, setNewQuestion] = useState('');
  const [dialogOpen, setDialogOpen] = useState({ text: false, url: false, pdf: false, siteUrl: false, pasteUrls: false, edit: false, conversation: false });
  const [widgetSettings, setWidgetSettings] = useState({ color: '#3B82F6', position: 'bottom-right' });
  const [editingKnowledge, setEditingKnowledge] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState('default');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [iconPreview, setIconPreview] = useState(null);
  const [conversationFilter, setConversationFilter] = useState('active'); // 'active' or 'archived'
  const [assistantInstructions, setAssistantInstructions] = useState('');
  const [jsonUploadDialog, setJsonUploadDialog] = useState(false);
  const [jsonFile, setJsonFile] = useState(null);
  const [jsonPreview, setJsonPreview] = useState(null);
  const [jsonUploadMode, setJsonUploadMode] = useState('merge'); // 'merge' or 'replace'
  const [activeTab, setActiveTab] = useState('analytics');
  const [formSettings, setFormSettings] = useState({
    enabled: true,
    trigger_phrases: ['contact', 'speak to someone', 'human', 'representative'],
    form_fields: { name: true, email: true, phone: false, message: true },
    notification_email: '',
    form_title: 'Contact Us',
    form_description: 'Fill out the form below and we\'ll get back to you soon.',
    success_message: 'Thank you! We\'ll be in touch shortly.'
  });
  const [newTriggerPhrase, setNewTriggerPhrase] = useState('');
  const [contactRequests, setContactRequests] = useState([]);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  
  // Pagination and bulk delete states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedConversationIds, setSelectedConversationIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Knowledge base bulk delete states
  const [selectedKnowledgeIds, setSelectedKnowledgeIds] = useState([]);
  const [selectAllKnowledge, setSelectAllKnowledge] = useState(false);
  
  // User profile settings
  const [userProfile, setUserProfile] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [chatbotName, setChatbotName] = useState('');
  const [scrapeTimelineKey, setScrapeTimelineKey] = useState(0);
  
  // Client email settings
  const [clientEmailSettings, setClientEmailSettings] = useState({
    recap_enabled: false,
    recap_email: ''
  });


  const iconOptions = [
    { id: 'default', name: 'Chat Bubble', icon: '💬' },
    { id: 'robot', name: 'Robot', icon: '🤖' },
    { id: 'headset', name: 'Headset', icon: '🎧' },
    { id: 'wave', name: 'Wave', icon: '👋' },
    { id: 'message', name: 'Message', icon: '💭' },
    { id: 'sparkle', name: 'Sparkle', icon: '✨' },
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [id]);

  // Listen for tab switch events from AnalyticsDashboard
  useEffect(() => {
    const handleSwitchTab = (event) => {
      setActiveTab(event.detail);
    };
    
    window.addEventListener('switchTab', handleSwitchTab);
    return () => window.removeEventListener('switchTab', handleSwitchTab);
  }, []);

  // Fetch conversations when conversations tab is active
  useEffect(() => {
    if (activeTab === 'conversations' && id) {
      fetchConversations(1);
    }
  }, [activeTab, id]);

  // Synchronize selectAll state with actual selection
  useEffect(() => {
    if (conversations.length > 0) {
      const filteredConvs = conversations.filter(conv => {
        if (conversationFilter === 'archived') {
          return conv.archived === true;
        }
        return !conv.archived;
      });
      
      const allSelected = filteredConvs.length > 0 && 
                         filteredConvs.every(conv => selectedConversationIds.includes(conv.id));
      setSelectAll(allSelected);
    }
  }, [selectedConversationIds, conversations, conversationFilter]);

  const fetchKnowledgeEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const knowledgeRes = await axios.get(`${API}/knowledge/${id}`, { headers });
      setKnowledge(knowledgeRes.data);
      toast.success('Knowledge base updated!');
    } catch (error) {
      console.error('Failed to refresh knowledge entries:', error);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [clientRes, knowledgeRes, questionsRes] = await Promise.all([
        axios.get(`${API}/clients/${id}`, { headers }),
        axios.get(`${API}/knowledge/${id}`, { headers }),
        axios.get(`${API}/starter-questions/${id}`, { headers })
      ]);
      
      setClient(clientRes.data);
      setKnowledge(knowledgeRes.data);
      const questions = questionsRes.data.questions || [];
      console.log('📊 Loaded starter questions:', questions);
      questions.forEach((q, i) => {
        console.log(`  ${i}. "${typeof q === 'string' ? q : q.question}" - show_on_widget:`, typeof q === 'object' ? q.show_on_widget : 'N/A (string)');
      });
      setStarterQuestions(questions);
      setChatbotName(clientRes.data.name || '');
      
      let settings = clientRes.data.widget_settings || { color: '#3B82F6', position: 'bottom-right' };
      
      // Migrate legacy autoOpen to new separate desktop/mobile fields (clone to avoid mutation)
      if (settings.autoOpen !== undefined) {
        settings = {
          ...settings,
          autoOpenDesktop: settings.autoOpenDesktop ?? settings.autoOpen,
          autoOpenMobile: settings.autoOpenMobile ?? settings.autoOpen
        };
      }
      
      setWidgetSettings(settings);
      setSelectedIcon(settings.icon || 'default');
      
      // Set icon preview if custom icon exists
      if (settings.customIconUrl) {
        setIconPreview(settings.customIconUrl);
      }
      
      // Load assistant instructions
      const instructionsRes = await axios.get(`${API}/clients/${id}/assistant-instructions`, { headers });
      setAssistantInstructions(instructionsRes.data.instructions || '');
      
      // Load form settings
      fetchFormSettings();
      fetchContactRequests();
      fetchUserProfile();
      fetchClientEmailSettings();
    } catch (error) {
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProfile({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        company_name: response.data.company_name || '',
        email: response.data.email || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Failed to load user profile');
    }
  };

  const fetchFormSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients/${id}/form-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormSettings(response.data);
    } catch (error) {
      console.error('Failed to load form settings');
    }
  };

  const fetchContactRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [requestsRes, countRes] = await Promise.all([
        axios.get(`${API}/contact-requests/${id}`, { headers }),
        axios.get(`${API}/contact-requests/${id}/unresolved-count`, { headers })
      ]);
      
      setContactRequests(requestsRes.data);
      setUnresolvedCount(countRes.data.count);
    } catch (error) {
      console.error('Failed to load contact requests');
    }
  };

  const fetchClientEmailSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients/${id}/email-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientEmailSettings({
        recap_enabled: response.data.recap_enabled || false,
        recap_email: response.data.recap_email || ''
      });
    } catch (error) {
      console.error('Failed to load email settings');
    }
  };

  const fetchConversations = async (page = 1) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/conversations/${id}`, {
        headers,
        params: { page, limit: 20 }
      });
      
      setConversations(response.data.conversations);
      setTotalPages(response.data.pagination.total_pages);
      setCurrentPage(page);
      setSelectedConversationIds([]); // Reset selection on page change
      setSelectAll(false);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load conversations');
    }
  };

  const handleAddText = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/knowledge/${id}`, 
        { type: 'text', title: newText.title, content: newText.content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Text added to knowledge base');
      setNewText({ title: '', content: '' });
      setDialogOpen({ ...dialogOpen, text: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to add text');
    }
  };

  const handleAddUrl = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/knowledge/${id}`, 
        { type: 'url', content: newUrl, title: newUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('URL content scraped and added');
      setNewUrl('');
      setDialogOpen({ ...dialogOpen, url: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scrape URL');
    }
  };

  const handleUploadPdf = async () => {
    if (!pdfFile) return;
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', pdfFile);
      
      await axios.post(`${API}/knowledge/${id}/upload-pdf`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('PDF processed and added');
      setPdfFile(null);
      setDialogOpen({ ...dialogOpen, pdf: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    }
  };

  const handleRefreshKnowledge = async (knowledgeId) => {
    try {
      const token = localStorage.getItem('token');
      toast.loading('Re-scraping content...');
      await axios.post(`${API}/knowledge/${knowledgeId}/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.dismiss();
      toast.success('Content refreshed successfully');
      fetchData();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to refresh content');
    }
  };

  const handleDeleteKnowledge = async (knowledgeId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/knowledge/${knowledgeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Knowledge deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDeleteClient = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Chatbot deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to delete client:', error);
      toast.error('Failed to delete chatbot');
    }
  };

  const handleEditKnowledge = (item) => {
    setEditingKnowledge(item);
    setNewText({ title: item.title, content: item.content });
    setDialogOpen({ ...dialogOpen, edit: true });
  };

  const handleUpdateKnowledge = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/knowledge/${editingKnowledge.id}`, 
        { type: editingKnowledge.type, title: newText.title, content: newText.content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Knowledge updated');
      setNewText({ title: '', content: '' });
      setDialogOpen({ ...dialogOpen, edit: false });
      setEditingKnowledge(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };


  const handleAddSiteUrl = async () => {
    if (!siteUrl) return;
    try {
      setIsCrawlingSite(true);
      const token = localStorage.getItem('token');
      toast.info('Starting site crawl... Initializing crawler...', { duration: 3000 });
      const response = await axios.post(`${API}/knowledge/${id}/site-url`, 
        { type: 'site_url', content: siteUrl, title: 'Site Content' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Store batch_id in localStorage so ScrapeTimeline can track progress
      if (response.data.batch_id) {
        localStorage.setItem(`scrape_batch_${id}`, response.data.batch_id);
        // Force timeline to appear by incrementing key
        setScrapeTimelineKey(prev => prev + 1);
      }
      
      toast.success(response.data.message || 'Site crawl started! Check progress below.', { duration: 5000 });
      setSiteUrl('');
      setDialogOpen({ ...dialogOpen, siteUrl: false });
      setIsCrawlingSite(false);
      // Switch to Knowledge Base tab so user can see progress
      setActiveTab('knowledge');
    } catch (error) {
      setIsCrawlingSite(false);
      toast.error(error.response?.data?.detail || 'Failed to start site crawl');
    }
  };

  const handlePasteUrls = async () => {
    if (!pastedUrls.trim()) return;
    
    // Parse URLs from pasted text (split by newlines, commas, or spaces)
    const urlList = pastedUrls
      .split(/[\n,\s]+/)
      .map(url => url.trim())
      .filter(url => url.startsWith('http'));
    
    if (urlList.length === 0) {
      toast.error('No valid URLs found. Please paste URLs starting with http:// or https://');
      return;
    }
    
    try {
      setIsProcessingUrls(true);
      const token = localStorage.getItem('token');
      
      // Show processing message
      toast.info(
        `Processing ${urlList.length} URLs... This may take a few minutes. You can close this dialog and continue working.`, 
        { duration: 10000 }
      );
      
      const response = await axios.post(`${API}/knowledge/${id}/paste-urls`, 
        { urls: urlList },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 } // 5 minute timeout
      );
      
      // Store batch_id in localStorage so ScrapeTimeline can track progress
      if (response.data.batch_id) {
        localStorage.setItem(`scrape_batch_${id}`, response.data.batch_id);
        // Force timeline to appear by incrementing key
        setScrapeTimelineKey(prev => prev + 1);
      }
      
      toast.success(response.data.message, { duration: 5000 });
      setPastedUrls('');
      setDialogOpen({ ...dialogOpen, pasteUrls: false });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to process URLs';
      toast.error(errorMsg);
      
      // If 403 error, suggest manual entry
      if (errorMsg.includes('403') || errorMsg.includes('Forbidden')) {
        setTimeout(() => {
          toast.info('💡 Tip: If pages are blocked, use "Add Text" to manually paste the content', { duration: 7000 });
        }, 2000);
      }
    } finally {
      setIsProcessingUrls(false);
    }
  };

  const handleEmailConversation = async () => {
    if (!emailAddress || !selectedConversation) return;
    
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/conversations/${selectedConversation.id}/email`, 
        { email: emailAddress },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Conversation transcript sent to ' + emailAddress);
      setEmailAddress('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSaveAssistantInstructions = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/clients/${id}/assistant-instructions`, 
        { instructions: assistantInstructions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Assistant instructions saved');
    } catch (error) {
      toast.error('Failed to save instructions');
    }
  };

  const handleSaveProfileSettings = async () => {
    try {
      // Validate password fields if user is trying to change password
      if (userProfile.new_password || userProfile.current_password || userProfile.confirm_password) {
        if (!userProfile.current_password) {
          toast.error('Current password is required to change password');
          return;
        }
        if (!userProfile.new_password) {
          toast.error('New password is required');
          return;
        }
        if (userProfile.new_password !== userProfile.confirm_password) {
          toast.error('New passwords do not match');
          return;
        }
        if (userProfile.new_password.length < 6) {
          toast.error('New password must be at least 6 characters');
          return;
        }
      }
      
      const token = localStorage.getItem('token');
      
      // Prepare update data
      const updateData = {
        first_name: userProfile.first_name,
        last_name: userProfile.last_name,
        company_name: userProfile.company_name,
        email: userProfile.email
      };
      
      // Only include password fields if user is changing password
      if (userProfile.new_password && userProfile.current_password) {
        updateData.current_password = userProfile.current_password;
        updateData.new_password = userProfile.new_password;
      }
      
      await axios.put(`${API}/auth/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Profile updated successfully');
      // Clear all password fields after successful update
      setUserProfile({ 
        ...userProfile, 
        current_password: '', 
        new_password: '', 
        confirm_password: '' 
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleSaveEmailSettings = async () => {
    // Validate email is provided when recap is enabled
    if (clientEmailSettings.recap_enabled && !clientEmailSettings.recap_email) {
      toast.error('Email address is required when conversation recaps are enabled');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/clients/${id}/email-settings`, 
        { 
          send_recap: clientEmailSettings.recap_enabled,
          email: clientEmailSettings.recap_email 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email settings saved successfully');
      
      // Refresh settings to confirm
      fetchClientEmailSettings();
    } catch (error) {
      // Handle Pydantic validation errors (array format)
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const errorMsg = error.response.data.detail.map(e => e.msg).join(', ');
        toast.error(errorMsg || 'Validation error');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save email settings');
      }
    }
  };


  const handleJsonFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        setJsonFile(json);
        
        // Auto-detect format and create preview
        let preview = json;
        if (json.intents) {
          // Convert intents format to items for preview
          preview = {
            type: 'intent',
            items: json.intents.map(intent => ({
              title: intent.name,
              question: intent.utterances ? intent.utterances[0] : '',
              answer: intent.response,
              content: intent.response,
              category: intent.name.split('.')[0]
            })),
            merge: true
          };
        }
        
        setJsonPreview(preview);
        setJsonUploadDialog(true);
      } catch (error) {
        toast.error('Invalid JSON file');
      }
    };
    reader.readAsText(file);
  };

  const handleJsonUpload = async () => {
    if (!jsonFile) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/knowledge/${id}/json-upload`,
        {
          ...jsonFile,
          merge: jsonUploadMode === 'merge'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} items had errors`);
      }
      
      setJsonUploadDialog(false);
      setJsonFile(null);
      setJsonPreview(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to upload JSON');
    }
  };

  const handleExportJson = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/knowledge/${id}/export-json`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `knowledge-base-${client?.name || 'export'}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Knowledge base exported');
    } catch (error) {
      toast.error('Failed to export knowledge base');
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Conversation deleted');
      fetchConversations(currentPage);
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const handleBulkDeleteConversations = async () => {
    if (selectedConversationIds.length === 0) {
      toast.error('No conversations selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedConversationIds.length} conversation(s)?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/conversations/bulk-delete`, 
        { conversation_ids: selectedConversationIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setSelectedConversationIds([]);
      setSelectAll(false);
      
      // Smart pagination: if we deleted all items on current page, go to previous page
      const filteredConvs = conversations.filter(conv => {
        if (conversationFilter === 'archived') {
          return conv.archived === true;
        }
        return !conv.archived;
      });
      
      const remainingOnPage = filteredConvs.length - selectedConversationIds.length;
      let targetPage = currentPage;
      
      // If current page will be empty and we're not on page 1, go to previous page
      if (remainingOnPage === 0 && currentPage > 1) {
        targetPage = currentPage - 1;
      }
      
      fetchConversations(targetPage);
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete conversations');
    }
  };

  const handleBulkDeleteKnowledge = async () => {
    if (selectedKnowledgeIds.length === 0) {
      toast.error('No knowledge items selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedKnowledgeIds.length} knowledge item(s)?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Delete each knowledge item (no bulk endpoint, so call delete multiple times)
      const deletePromises = selectedKnowledgeIds.map(knowledgeId =>
        axios.delete(`${API}/knowledge/${knowledgeId}`, 
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      
      await Promise.all(deletePromises);
      
      toast.success(`${selectedKnowledgeIds.length} knowledge item(s) deleted successfully`);
      setSelectedKnowledgeIds([]);
      setSelectAllKnowledge(false);
      fetchData();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete knowledge items');
    }
  };

  const toggleKnowledgeSelection = (knowledgeId) => {
    setSelectedKnowledgeIds(prev => 
      prev.includes(knowledgeId)
        ? prev.filter(id => id !== knowledgeId)
        : [...prev, knowledgeId]
    );
  };

  const toggleSelectAllKnowledge = () => {
    if (selectAllKnowledge) {
      setSelectedKnowledgeIds([]);
    } else {
      setSelectedKnowledgeIds(knowledge.map(k => k.id));
    }
    setSelectAllKnowledge(!selectAllKnowledge);
  };

  const handleArchiveConversation = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/conversations/${conversationId}/archive`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Conversation archived');
      fetchConversations(currentPage);
    } catch (error) {
      toast.error('Failed to archive conversation');
    }
  };

  const handleUnarchiveConversation = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/conversations/${conversationId}/unarchive`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Conversation restored');
      fetchConversations(currentPage);
    } catch (error) {
      toast.error('Failed to restore conversation');
    }
  };

  const handleSelectConversation = (conversationId) => {
    setSelectedConversationIds(prev => {
      if (prev.includes(conversationId)) {
        return prev.filter(id => id !== conversationId);
      } else {
        return [...prev, conversationId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedConversationIds([]);
    } else {
      const filteredConvs = conversations.filter(conv => {
        if (conversationFilter === 'archived') {
          return conv.archived === true;
        }
        return !conv.archived;
      });
      setSelectedConversationIds(filteredConvs.map(conv => conv.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSaveGuidelines = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/clients/${id}/guidelines`, 
        { guidelines: chatbotGuidelines },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Guidelines saved');
    } catch (error) {
      toast.error('Failed to save guidelines');
    }
  };

  const handleSaveQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/starter-questions/${id}`, 
        { questions: starterQuestions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Starter questions saved');
    } catch (error) {
      toast.error('Failed to save questions');
    }
  };

  const handleAddQuestion = () => {
    if (newQuestion.trim()) {
      setStarterQuestions([...starterQuestions, { question: newQuestion.trim(), answer: "", resource_urls: [] }]);
      setNewQuestion('');
    }
  };

  const handleEditAnswer = (index) => {
    const item = starterQuestions[index];
    setEditingAnswer({ 
      index, 
      question: item.question, 
      answer: item.answer || "",
      resource_urls: item.resource_urls || []
    });
    setSuggestedAnswer("");
  };

  const handleSaveAnswer = async () => {
    if (editingAnswer !== null) {
      // Filter out incomplete resource links (must have a URL to be valid)
      const validResourceUrls = (editingAnswer.resource_urls || []).filter(
        link => link && link.url && link.url.trim() !== ''
      );
      
      const newQuestions = [...starterQuestions];
      newQuestions[editingAnswer.index] = {
        question: editingAnswer.question,
        answer: editingAnswer.answer,
        resource_urls: validResourceUrls
      };
      setStarterQuestions(newQuestions);
      setEditingAnswer(null);
      setSuggestedAnswer("");
      
      // Auto-save to database immediately
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/starter-questions/${id}`, 
          { questions: newQuestions },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Answer and resource links saved successfully');
      } catch (error) {
        toast.error('Failed to save changes');
        console.error('Save error:', error);
      }
    }
  };

  const handleSuggestAnswer = async () => {
    if (!editingAnswer) return;
    
    setLoadingSuggestion(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/starter-questions/${id}/suggest-answer`,
        { question: editingAnswer.question },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const suggestedAnswer = response.data.suggested_answer;
      const suggestedUrls = response.data.suggested_resource_urls || [];
      
      setSuggestedAnswer(suggestedAnswer);
      
      // Auto-fill the suggested answer and resource URLs
      setEditingAnswer(prev => ({ 
        ...prev, 
        answer: suggestedAnswer,
        resource_urls: suggestedUrls.length > 0 ? suggestedUrls : prev.resource_urls
      }));
      
      if (suggestedUrls.length > 0) {
        toast.success(`Suggested answer with ${suggestedUrls.length} resource link(s)`);
      } else {
        toast.success('Suggested answer generated');
      }
    } catch (error) {
      console.error('Failed to suggest answer:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate suggestion';
      toast.error(errorMessage);
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleRegenerateEmbeddings = async () => {
    setRegeneratingEmbeddings(true);
    toast.info('Generating semantic embeddings for all questions and aliases...');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/starter-questions/${id}/regenerate-embeddings`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { total_embeddings, questions, aliases} = response.data;
      toast.success(
        `Successfully generated ${total_embeddings} embeddings (${questions} questions + ${aliases} aliases)`
      );
    } catch (error) {
      console.error('Failed to regenerate embeddings:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to regenerate embeddings';
      toast.error(errorMessage);
    } finally {
      setRegeneratingEmbeddings(false);
    }
  };

  const handleRegenerateKBEmbeddings = async () => {
    setRegeneratingKBEmbeddings(true);
    toast.info('Regenerating knowledge base embeddings in the background... This may take a few minutes. You can continue working while this processes.', {
      duration: 5000
    });
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/knowledge/${id}/regenerate-embeddings`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { total_entries, total_chunks, success_count } = response.data;
      toast.success(
        `Embeddings complete! Generated for ${success_count}/${total_entries} entries (${total_chunks} chunks)`,
        { duration: 6000 }
      );
      
      await fetchData();
    } catch (error) {
      console.error('Failed to regenerate KB embeddings:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to regenerate embeddings';
      
      if (errorMessage.includes('Network Error') || errorMessage.includes('timeout')) {
        toast.info('Embeddings are still generating in the background. This may take a few minutes for large knowledge bases. Check back shortly!', {
          duration: 6000
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setRegeneratingKBEmbeddings(false);
    }
  };

  // Helper function to extract readable text from message content
  const getMessagePreview = (content) => {
    try {
      // Try to parse as JSON (for assistant messages)
      const parsed = JSON.parse(content);
      if (parsed.answer) {
        // Strip HTML tags for preview
        return parsed.answer.replace(/<[^>]*>/g, '');
      }
      return content;
    } catch {
      // If not JSON, return as-is (for user messages)
      return content;
    }
  };

  const handleRemoveQuestion = (index) => {
    setStarterQuestions(starterQuestions.filter((_, i) => i !== index));
  };

  const handleToggleVisibility = async (index) => {
    try {
      const token = localStorage.getItem('token');
      const currentQuestion = starterQuestions[index];
      // Treat undefined as true (default), so we properly toggle from shown to hidden
      const currentVisibility = currentQuestion.show_on_widget !== false;
      const newVisibility = !currentVisibility;
      
      // Update locally first for immediate UI feedback
      const updatedQuestions = [...starterQuestions];
      updatedQuestions[index] = { ...updatedQuestions[index], show_on_widget: newVisibility };
      setStarterQuestions(updatedQuestions);
      
      // Update on server
      await axios.post(
        `${API}/starter-questions/${id}/toggle-visibility`,
        { question_index: index, show_on_widget: newVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(newVisibility ? 'Question will show on widget' : 'Question hidden from widget');
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast.error('Failed to update visibility');
      // Revert on error
      fetchData();
    }
  };

  // Drag and drop handlers for starter questions
  const handleDragStart = (index) => {
    setDraggedQuestionIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedQuestionIndex === null || draggedQuestionIndex === index) return;
    
    // Reorder the array
    const newQuestions = [...starterQuestions];
    const draggedItem = newQuestions[draggedQuestionIndex];
    newQuestions.splice(draggedQuestionIndex, 1);
    newQuestions.splice(index, 0, draggedItem);
    
    setStarterQuestions(newQuestions);
    setDraggedQuestionIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedQuestionIndex(null);
  };

  const handleSaveDesign = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Upload custom icon if provided
      let iconUrl = widgetSettings.customIconUrl;
      if (iconFile) {
        const formData = new FormData();
        formData.append('file', iconFile);
        formData.append('client_id', id);
        
        const uploadRes = await axios.post(`${API}/clients/${id}/upload-icon`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        iconUrl = uploadRes.data.icon_url;
      }
      
      const updatedSettings = {
        ...widgetSettings,
        icon: selectedIcon,
        customIconUrl: iconUrl
      };
      
      // Ensure new auto-open fields are set (for backward compatibility migration)
      if (updatedSettings.autoOpenDesktop === undefined && updatedSettings.autoOpen !== undefined) {
        updatedSettings.autoOpenDesktop = updatedSettings.autoOpen;
      }
      if (updatedSettings.autoOpenMobile === undefined && updatedSettings.autoOpen !== undefined) {
        updatedSettings.autoOpenMobile = updatedSettings.autoOpen;
      }
      
      await axios.put(`${API}/clients/${id}`, 
        { name: chatbotName, domain: client.domain, widget_settings: updatedSettings },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Design settings saved');
      fetchData();
    } catch (error) {
      toast.error('Failed to save design');
    }
  };

  const handleSaveFormSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/clients/${id}/form-settings`,
        formSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Form settings saved');
      fetchFormSettings();
    } catch (error) {
      toast.error('Failed to save form settings');
    }
  };


  // Use the clean Elfsight-style embed code
  // Use BACKEND_URL for widget script src
  // In development: http://localhost:8000
  // In production: Set via build environment variable to your deployment URL
  const widgetUrl = BACKEND_URL || window.location.origin;
  // Remove version parameter to avoid WordPress mangling the URL
  const widgetCode = client?.client_number ? 
`<!-- IcebreakChat Widget -->
<script src="${widgetUrl}/widget.js" data-icebreak-id="${client.client_number}" async></script>
<div class="icebreak-widget"></div>` : '';

  const handleCopyCode = () => {
    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(widgetCode)
        .then(() => {
          toast.success('Code copied to clipboard');
        })
        .catch((err) => {
          // Fallback to older method
          copyToClipboardFallback(widgetCode);
        });
    } else {
      // Fallback for browsers that don't support Clipboard API
      copyToClipboardFallback(widgetCode);
    }
  };

  const copyToClipboardFallback = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success('Code copied to clipboard');
      } else {
        toast.error('Failed to copy code');
      }
    } catch (err) {
      toast.error('Failed to copy code');
    }
    
    document.body.removeChild(textArea);
  };

  if (loading) return <div className="p-8" data-testid="loading-spinner">Loading...</div>;
  if (!client) return <div className="p-8">Client not found</div>;

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-blue-500 p-3 rounded-xl">
                <MessageSquare className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }} data-testid="client-name">{client.name}</h1>
                <p className="text-gray-600 flex items-center mt-1">
                  <Globe className="w-4 h-4 mr-1" /> {client.domain}
                </p>
              </div>
            </div>
            {/* Chatbot Settings Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                  data-testid="client-settings-dropdown"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-lg"
                    style={{ backgroundColor: widgetSettings.color || '#3B82F6' }}
                  >
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium">{client.name}</div>
                    <div className="text-xs text-gray-500">Chatbot Settings</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Chatbot Options</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setActiveTab('embed')}>
                  <Code className="w-4 h-4 mr-2" />
                  Embed Code
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveTab('design')}>
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  Design Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveTab('form')}>
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Form
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveTab('settings')}>
                  <User className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to delete "${client.name}"? This action cannot be undone.`)) {
                      handleDeleteClient();
                    }
                  }}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Chatbot
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="analytics" data-testid="analytics-tab">Analytics</TabsTrigger>
            <TabsTrigger value="knowledge" data-testid="knowledge-tab">Knowledge Base</TabsTrigger>
            <TabsTrigger value="questions" data-testid="questions-tab">Starter Questions</TabsTrigger>
            <TabsTrigger value="preview" data-testid="preview-tab">Preview</TabsTrigger>
            <TabsTrigger value="conversations" data-testid="conversations-tab">Conversations</TabsTrigger>
            <TabsTrigger value="form" data-testid="form-tab" className="relative">
              Contact Form
              {unresolvedCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                  {unresolvedCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4">
            <AnalyticsDashboard clientId={id} />
          </TabsContent>

          {/* Knowledge Base Tab */}
          <TabsContent value="knowledge" className="space-y-4">
            {/* Scrape Timeline */}
            <ScrapeTimeline key={scrapeTimelineKey} clientId={id} onComplete={fetchKnowledgeEntries} />
            
            {/* Assistant Instructions Section */}
            <Card className="glass border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-lg">Assistant Instructions</CardTitle>
                <CardDescription>Define general guidelines for how the chatbot should respond</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  data-testid="assistant-instructions-input"
                  value={assistantInstructions}
                  onChange={(e) => setAssistantInstructions(e.target.value)}
                  placeholder="e.g., Always be polite and professional. Keep responses under 3 sentences when possible."
                  rows={8}
                  className="resize-y min-h-[120px] max-h-[600px]"
                />
                <Button 
                  onClick={handleSaveAssistantInstructions} 
                  data-testid="save-instructions-button"
                  size="sm"
                >
                  Save Instructions
                </Button>
              </CardContent>
            </Card>

            {/* Crawl Progress Bar */}
            <CrawlProgressBar clientId={id} onComplete={fetchKnowledgeEntries} />

            <Card className="glass">
              <CardHeader>
                <CardTitle>Knowledge Base</CardTitle>
                <CardDescription>Upload content for your AI to learn from</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-4 gap-4">
                  {/* Add Text */}
                  <Dialog open={dialogOpen.text} onOpenChange={(open) => setDialogOpen({ ...dialogOpen, text: open })}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="h-24" data-testid="add-text-button">
                        <div className="text-center">
                          <FileText className="w-8 h-8 mx-auto mb-2" />
                          <div>Add Text</div>
                        </div>
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="add-text-dialog">
                      <DialogHeader>
                        <DialogTitle>Add Text Content</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Title</Label>
                          <Input
                            data-testid="text-title-input"
                            value={newText.title}
                            onChange={(e) => setNewText({ ...newText, title: e.target.value })}
                            placeholder="FAQ: Returns Policy"
                          />
                        </div>
                        <div>
                          <Label>Content</Label>
                          <Textarea
                            data-testid="text-content-input"
                            value={newText.content}
                            onChange={(e) => setNewText({ ...newText, content: e.target.value })}
                            placeholder="Enter your content here..."
                            rows={6}
                          />
                        </div>
                        <Button onClick={handleAddText} className="w-full" data-testid="save-text-button">Add to Knowledge Base</Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Add URL */}
                  <Dialog open={dialogOpen.url} onOpenChange={(open) => setDialogOpen({ ...dialogOpen, url: open })}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="h-24" data-testid="add-url-button">
                        <div className="text-center">
                          <LinkIcon className="w-8 h-8 mx-auto mb-2" />
                          <div>Add URL</div>
                        </div>
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="add-url-dialog">
                      <DialogHeader>
                        <DialogTitle>Scrape URL Content</DialogTitle>
                        <DialogDescription>We'll extract text content from the URL</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Input
                          data-testid="url-input"
                          value={newUrl}
                          onChange={(e) => setNewUrl(e.target.value)}
                          placeholder="https://example.com/faq"
                        />
                        <Button onClick={handleAddUrl} className="w-full" data-testid="scrape-url-button">Scrape & Add</Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Upload PDF */}
                  <Dialog open={dialogOpen.pdf} onOpenChange={(open) => setDialogOpen({ ...dialogOpen, pdf: open })}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="h-24" data-testid="upload-pdf-button">
                        <div className="text-center">
                          <Upload className="w-8 h-8 mx-auto mb-2" />
                          <div>Upload PDF</div>
                        </div>
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="upload-pdf-dialog">
                      <DialogHeader>
                        <DialogTitle>Upload PDF Document</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Input
                          type="file"
                          accept=".pdf"
                          data-testid="pdf-file-input"
                          onChange={(e) => setPdfFile(e.target.files[0])}
                        />
                        <Button onClick={handleUploadPdf} className="w-full" disabled={!pdfFile} data-testid="upload-pdf-submit-button">
                          Upload & Process
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Add Site URL */}
                  <Dialog open={dialogOpen.siteUrl} onOpenChange={(open) => setDialogOpen({ ...dialogOpen, siteUrl: open })}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="h-24" data-testid="add-site-url-button">
                        <div className="text-center">
                          <Globe className="w-8 h-8 mx-auto mb-2" />
                          <div>Crawl Site</div>
                          <div className="text-xs text-gray-500">Spider entire site</div>
                        </div>
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="add-site-url-dialog">
                      <DialogHeader>
                        <DialogTitle>Crawl Entire Website</DialogTitle>
                        <DialogDescription>Automatically discover and scrape up to 300 pages (200,000 words) from your website</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Input
                          data-testid="site-url-input"
                          value={siteUrl}
                          onChange={(e) => setSiteUrl(e.target.value)}
                          placeholder="https://example.com"
                          disabled={isCrawlingSite}
                        />
                        
                        {isCrawlingSite && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-blue-600">
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              <span>Initializing site crawler...</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                              <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                            </div>
                            <p className="text-xs text-gray-500">
                              This will only take a moment. The crawl will continue in the background after you click "Start Crawling".
                            </p>
                          </div>
                        )}
                        
                        {!isCrawlingSite && (
                          <>
                            <div className="text-sm text-gray-600">
                              <p className="mb-2">The crawler will:</p>
                              <ul className="list-disc list-inside space-y-1">
                                <li>Start from the URL provided</li>
                                <li>Follow all internal links automatically</li>
                                <li>Extract content from each page</li>
                                <li>Limit to 300 pages or 200,000 words</li>
                                <li>Filter out external competitor links</li>
                              </ul>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                              <p className="text-sm text-blue-800">
                                💡 <strong>Real-time progress tracking:</strong> View live status updates below as each page is discovered and scraped. You can continue working while the crawl runs in the background.
                              </p>
                            </div>
                          </>
                        )}
                        
                        <Button 
                          onClick={handleAddSiteUrl} 
                          className="w-full" 
                          data-testid="extract-site-button"
                          disabled={isCrawlingSite || !siteUrl}
                        >
                          {isCrawlingSite ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Initializing...
                            </>
                          ) : (
                            'Start Crawling'
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Paste URLs */}
                  <Dialog open={dialogOpen.pasteUrls} onOpenChange={(open) => setDialogOpen({ ...dialogOpen, pasteUrls: open })}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="h-24" data-testid="paste-urls-button">
                        <div className="text-center">
                          <FileText className="w-8 h-8 mx-auto mb-2" />
                          <div>Paste URLs</div>
                          <div className="text-xs text-gray-500">Bulk scrape</div>
                        </div>
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="paste-urls-dialog">
                      <DialogHeader>
                        <DialogTitle>Paste & Scrape URLs</DialogTitle>
                        <DialogDescription>Paste a list of URLs (one per line) to scrape content from each page</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Textarea
                          data-testid="paste-urls-input"
                          value={pastedUrls}
                          onChange={(e) => setPastedUrls(e.target.value)}
                          placeholder="https://example.com/page1&#10;https://example.com/page2&#10;https://example.com/page3"
                          rows={8}
                          disabled={isProcessingUrls}
                        />
                        <div className="text-sm text-gray-600 space-y-2">
                          <p className="mb-2">💡 Perfect for bulk URL scraping</p>
                          <p>Paste URLs separated by newlines or commas</p>
                          {isProcessingUrls && (
                            <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-1">
                              <p className="font-semibold text-blue-800">⏳ Processing URLs...</p>
                              <p className="text-blue-700">This may take a few minutes. Please avoid pasting again.</p>
                              <p className="text-blue-700">You can close this dialog and continue working.</p>
                            </div>
                          )}
                        </div>
                        <Button 
                          onClick={handlePasteUrls} 
                          className="w-full" 
                          data-testid="process-urls-button"
                          disabled={isProcessingUrls}
                        >
                          {isProcessingUrls ? 'Processing...' : 'Scrape URLs'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* JSON Import/Export Section */}
                <div className="grid md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
                  <div>
                    <input
                      type="file"
                      accept=".json"
                      id="json-upload"
                      className="hidden"
                      onChange={handleJsonFileSelect}
                    />
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => document.getElementById('json-upload').click()}
                      data-testid="upload-json-button"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload JSON
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleExportJson}
                    data-testid="export-json-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export as JSON
                  </Button>
                </div>

                {/* Knowledge List */}
                <div className="space-y-2 mt-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold">Uploaded Content ({knowledge.length})</h3>
                    
                    {knowledge.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRegenerateKBEmbeddings}
                          disabled={regeneratingKBEmbeddings}
                          data-testid="regenerate-kb-embeddings-button"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${regeneratingKBEmbeddings ? 'animate-spin' : ''}`} />
                          {regeneratingKBEmbeddings ? 'Generating...' : 'Regenerate Embeddings'}
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={toggleSelectAllKnowledge}
                          data-testid="select-all-knowledge-button"
                        >
                          {selectAllKnowledge ? 'Deselect All' : 'Select All'}
                        </Button>
                        
                        {selectedKnowledgeIds.length > 0 && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleBulkDeleteKnowledge}
                            data-testid="bulk-delete-knowledge-button"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete ({selectedKnowledgeIds.length})
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {knowledge.length === 0 ? (
                    <p className="text-gray-500 text-center py-8" data-testid="no-knowledge-message">No content yet. Add some knowledge for your AI!</p>
                  ) : (
                    knowledge.map((item) => (
                      <Card key={item.id} className="p-4" data-testid={`knowledge-item-${item.id}`}>
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedKnowledgeIds.includes(item.id)}
                            onChange={() => toggleKnowledgeSelection(item.id)}
                            className="mt-1 w-4 h-4 cursor-pointer"
                            data-testid={`checkbox-knowledge-${item.id}`}
                          />
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {item.type === 'text' && <FileText className="w-4 h-4" />}
                              {item.type === 'url' && <LinkIcon className="w-4 h-4" />}
                              {item.type === 'pdf' && <Upload className="w-4 h-4" />}
                              {item.type === 'site_url' && <Network className="w-4 h-4 text-purple-600" />}
                              <span className="font-medium">{item.title}</span>
                              {item.updated_at && (
                                <span className="text-xs text-gray-400 ml-2">
                                  (Last scraped: {new Date(item.updated_at).toLocaleDateString()})
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 line-clamp-2">{item.content.substring(0, 150)}...</p>
                          </div>
                          
                          <div className="flex gap-2">
                            {(item.type === 'url' || item.type === 'site_url') && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRefreshKnowledge(item.id)}
                                data-testid={`refresh-knowledge-${item.id}`}
                                title="Re-scrape content from URL"
                              >
                                <RefreshCw className="w-4 h-4 text-green-500" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditKnowledge(item)}
                              data-testid={`edit-knowledge-${item.id}`}
                            >
                              <Edit className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteKnowledge(item.id)}
                              data-testid={`delete-knowledge-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))
                  )}
                </div>

                {/* Edit Knowledge Dialog */}
                <Dialog open={dialogOpen.edit} onOpenChange={(open) => setDialogOpen({ ...dialogOpen, edit: open })}>
                  <DialogContent data-testid="edit-knowledge-dialog">
                    <DialogHeader>
                      <DialogTitle>Edit Knowledge</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Title</Label>
                        <Input
                          data-testid="edit-title-input"
                          value={newText.title}
                          onChange={(e) => setNewText({ ...newText, title: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Content</Label>
                        <Textarea
                          data-testid="edit-content-input"
                          value={newText.content}
                          onChange={(e) => setNewText({ ...newText, content: e.target.value })}
                          rows={6}
                        />
                      </div>
                      <Button onClick={handleUpdateKnowledge} className="w-full" data-testid="update-knowledge-button">
                        Update Knowledge
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

            {/* JSON Upload Preview Dialog */}
            <Dialog open={jsonUploadDialog} onOpenChange={setJsonUploadDialog}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Preview JSON Upload</DialogTitle>
                  <DialogDescription>
                    Review the data before uploading to your knowledge base
                  </DialogDescription>
                </DialogHeader>
                {jsonPreview && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">Type:</span>
                        <span className="text-blue-600">{jsonPreview.type || 'custom'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Items:</span>
                        <span className="text-blue-600">{jsonPreview.items?.length || 0}</span>
                      </div>
                    </div>

                    <div>
                      <Label>Upload Mode</Label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="uploadMode"
                            value="merge"
                            checked={jsonUploadMode === 'merge'}
                            onChange={(e) => setJsonUploadMode(e.target.value)}
                          />
                          <span>Merge (add to existing)</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="uploadMode"
                            value="replace"
                            checked={jsonUploadMode === 'replace'}
                            onChange={(e) => setJsonUploadMode(e.target.value)}
                          />
                          <span className="text-red-600">Replace (delete existing)</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <Label>Preview (first 3 items)</Label>
                      <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                        {jsonPreview.items?.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="bg-gray-50 p-3 rounded text-sm">
                            {item.question && <div><strong>Q:</strong> {item.question}</div>}
                            {item.answer && <div><strong>A:</strong> {item.answer}</div>}
                            {item.title && <div><strong>Title:</strong> {item.title}</div>}
                            {item.content && <div><strong>Content:</strong> {item.content.substring(0, 100)}...</div>}
                            {item.category && <div className="text-xs text-gray-500 mt-1">Category: {item.category}</div>}
                          </div>
                        ))}
                        {jsonPreview.items?.length > 3 && (
                          <div className="text-center text-sm text-gray-500">
                            ... and {jsonPreview.items.length - 3} more items
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={handleJsonUpload} className="flex-1">
                        Upload {jsonPreview.items?.length} Items
                      </Button>
                      <Button variant="outline" onClick={() => setJsonUploadDialog(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Starter Questions Tab */}
          <TabsContent value="questions" className="space-y-4">
            <Card className="glass">
              <CardHeader>
                <CardTitle>Starter Questions</CardTitle>
                <CardDescription>Help visitors break the ice with suggested questions. Drag to reorder.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {starterQuestions.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-blue-900 mb-1">Semantic Matching</h4>
                        <p className="text-sm text-blue-700">
                          Click "Regenerate Embeddings" after adding, editing, or removing questions to enable intelligent semantic matching. 
                          This helps the chatbot understand questions phrased differently (e.g., "how do I order?" matches "how can I place an order?").
                        </p>
                      </div>
                      <Button
                        onClick={handleRegenerateEmbeddings}
                        disabled={regeneratingEmbeddings}
                        variant="outline"
                        size="sm"
                        className="bg-white hover:bg-blue-50 border-blue-300 text-blue-700 whitespace-nowrap"
                      >
                        {regeneratingEmbeddings ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Regenerate Embeddings
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Input
                    data-testid="new-question-input"
                    placeholder="What's your return policy?"
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddQuestion()}
                  />
                  <Button onClick={handleAddQuestion} data-testid="add-question-button">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  {starterQuestions.length === 0 ? (
                    <p className="text-gray-500 text-center py-8" data-testid="no-questions-message">No starter questions yet</p>
                  ) : (
                    starterQuestions.map((item, index) => {
                      const question = typeof item === 'string' ? item : item.question;
                      const hasAnswer = typeof item === 'object' && item.answer && item.answer.trim().length > 0;
                      
                      return (
                        <Card 
                          key={index} 
                          className={`p-3 flex items-center gap-3 transition-all ${
                            draggedQuestionIndex === index ? 'opacity-50 scale-95' : 'hover:shadow-md cursor-move'
                          }`}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragEnd={handleDragEnd}
                          data-testid={`question-item-${index}`}
                        >
                          <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0 cursor-grab active:cursor-grabbing" />
                          <div className="flex-1">
                            <div className="font-medium">{question}</div>
                            {hasAnswer && (
                              <div className="text-xs text-green-600 mt-1">✓ Answer configured</div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleVisibility(index)}
                            title={item.show_on_widget !== false ? "Hide from widget" : "Show on widget"}
                          >
                            <Star 
                              className={`w-4 h-4 transition-colors ${
                                item.show_on_widget !== false 
                                  ? 'text-yellow-500 fill-yellow-500' 
                                  : 'text-gray-300'
                              }`} 
                            />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditAnswer(index)}
                            title="Edit answer"
                          >
                            <Edit className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveQuestion(index)}
                            data-testid={`remove-question-${index}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </Card>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Answer Editor Dialog */}
            <Dialog open={editingAnswer !== null} onOpenChange={(open) => !open && setEditingAnswer(null)}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Answer for Starter Question</DialogTitle>
                  <DialogDescription>
                    Configure a pre-written answer for this common question. Click "Suggest Answer" to let AI generate one for you.
                  </DialogDescription>
                </DialogHeader>
                
                {editingAnswer && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="question-text" className="font-semibold">Question:</Label>
                      <Input
                        id="question-text"
                        className="mt-1"
                        value={editingAnswer.question}
                        onChange={(e) => setEditingAnswer(prev => ({ ...prev, question: e.target.value }))}
                        placeholder="Enter your question..."
                      />
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <Label htmlFor="answer-text">Answer:</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSuggestAnswer}
                          disabled={loadingSuggestion}
                        >
                          {loadingSuggestion ? (
                            <>
                              <span className="animate-spin mr-2">⟳</span>
                              Generating...
                            </>
                          ) : (
                            <>
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Suggest Answer with AI
                            </>
                          )}
                        </Button>
                      </div>
                      <textarea
                        id="answer-text"
                        className="w-full h-32 p-3 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter the answer or click 'Suggest Answer' to generate one..."
                        value={editingAnswer.answer}
                        onChange={(e) => setEditingAnswer(prev => ({ ...prev, answer: e.target.value }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This answer will be used when visitors click this starter question.
                      </p>
                    </div>
                    
                    {/* Resource URLs Section */}
                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <Label className="font-semibold text-base">Resource Links (Optional - Up to 5)</Label>
                          <p className="text-xs text-gray-500">Add helpful links to be displayed with the answer</p>
                        </div>
                        {(!editingAnswer.resource_urls || editingAnswer.resource_urls.length < 5) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newUrls = [...(editingAnswer.resource_urls || [{ title: '', description: '', url: '' }])];
                              newUrls.push({ title: '', description: '', url: '' });
                              setEditingAnswer(prev => ({ ...prev, resource_urls: newUrls }));
                            }}
                          >
                            + Add Link
                          </Button>
                        )}
                      </div>
                      
                      {(editingAnswer.resource_urls && editingAnswer.resource_urls.length > 0 ? editingAnswer.resource_urls : [{ title: '', description: '', url: '' }]).map((urlData, urlIndex) => {
                        return (
                          <div key={urlIndex} className="mb-4 p-3 border rounded-md bg-gray-50 relative">
                            <div className="flex justify-between items-center mb-2">
                              <Label className="text-sm font-medium">Link {urlIndex + 1}</Label>
                              {(editingAnswer.resource_urls?.length > 1) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newUrls = editingAnswer.resource_urls.filter((_, i) => i !== urlIndex);
                                    setEditingAnswer(prev => ({ ...prev, resource_urls: newUrls }));
                                  }}
                                  className="h-6 text-red-500 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                            <div className="space-y-2">
                              <div>
                                <Label htmlFor={`url-title-${urlIndex}`} className="text-xs">Title</Label>
                                <input
                                  id={`url-title-${urlIndex}`}
                                  type="text"
                                  className="w-full p-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., Product Catalog"
                                  value={urlData.title}
                                  onChange={(e) => {
                                    const newUrls = [...(editingAnswer.resource_urls || [{ title: '', description: '', url: '' }])];
                                    newUrls[urlIndex] = { ...urlData, title: e.target.value };
                                    setEditingAnswer(prev => ({ ...prev, resource_urls: newUrls }));
                                  }}
                                />
                              </div>
                              <div>
                                <Label htmlFor={`url-desc-${urlIndex}`} className="text-xs">Description</Label>
                                <input
                                  id={`url-desc-${urlIndex}`}
                                  type="text"
                                  className="w-full p-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., Browse our full collection"
                                  value={urlData.description}
                                  onChange={(e) => {
                                    const newUrls = [...(editingAnswer.resource_urls || [{ title: '', description: '', url: '' }])];
                                    newUrls[urlIndex] = { ...urlData, description: e.target.value };
                                    setEditingAnswer(prev => ({ ...prev, resource_urls: newUrls }));
                                  }}
                                />
                              </div>
                              <div>
                                <Label htmlFor={`url-link-${urlIndex}`} className="text-xs">URL</Label>
                                <input
                                  id={`url-link-${urlIndex}`}
                                  type="url"
                                  className="w-full p-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="https://yourwebsite.com/page"
                                  value={urlData.url}
                                  onChange={(e) => {
                                    const newUrls = [...(editingAnswer.resource_urls || [{ title: '', description: '', url: '' }])];
                                    newUrls[urlIndex] = { ...urlData, url: e.target.value };
                                    setEditingAnswer(prev => ({ ...prev, resource_urls: newUrls }));
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="flex gap-2 justify-end pt-4 border-t">
                      <Button variant="outline" onClick={() => setEditingAnswer(null)}>
                        Cancel
                      </Button>
                      <Button onClick={handleSaveAnswer}>
                        Save Answer
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Design Tab */}
          <TabsContent value="design" className="space-y-4">
            <Card className="glass">
              <CardHeader>
                <CardTitle>Widget Design</CardTitle>
                <CardDescription>Customize your chatbot's appearance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  {/* Preview of colors */}
                  <div className="border rounded-lg p-4 space-y-3 bg-blue-50">
                    <p className="text-sm font-medium mb-2">Preview:</p>
                    <div className="flex gap-2 items-center">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-2xl overflow-hidden"
                        style={{ backgroundColor: widgetSettings.color }}
                      >
                        {selectedIcon === 'custom' && iconPreview ? (
                          <img 
                            src={iconPreview} 
                            alt="Custom icon" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-white">
                            {iconOptions.find(i => i.id === selectedIcon)?.icon || '💬'}
                          </span>
                        )}
                      </div>
                      <span className="text-sm">Chat Button</span>
                    </div>
                    <div className="flex gap-2">
                      <div 
                        className="px-4 py-2 rounded-lg text-white text-sm"
                        style={{ backgroundColor: widgetSettings.color }}
                      >
                        User message bubble
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="primary-color">Primary Color</Label>
                    <div className="flex gap-3 items-center">
                      <Input
                        id="primary-color"
                        type="color"
                        data-testid="primary-color-input"
                        value={widgetSettings.color}
                        onChange={(e) => setWidgetSettings({ ...widgetSettings, color: e.target.value })}
                        className="w-20 h-10 cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={widgetSettings.color}
                        onChange={(e) => setWidgetSettings({ ...widgetSettings, color: e.target.value })}
                        placeholder="#3B82F6"
                        className="flex-1"
                      />
                    </div>
                    <p className="text-sm text-gray-500">Used for header, buttons, and user messages</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Chat Button Icon</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {iconOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => setSelectedIcon(option.id)}
                          className={`p-4 border-2 rounded-lg text-center hover:border-blue-500 transition-colors ${
                            selectedIcon === option.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                          }`}
                          data-testid={`icon-${option.id}`}
                        >
                          <div className="text-3xl mb-1">{option.icon}</div>
                          <div className="text-xs text-gray-600">{option.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="custom-icon">Upload Custom Icon (optional)</Label>
                    <Input
                      id="custom-icon"
                      type="file"
                      accept="image/*"
                      data-testid="custom-icon-input"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setIconFile(file);
                          setSelectedIcon('custom');
                          
                          // Create preview URL
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setIconPreview(reader.result);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <p className="text-sm text-gray-500">Recommended: 64x64px PNG with transparent background</p>
                    
                    {/* Preview uploaded icon */}
                    {iconPreview && selectedIcon === 'custom' && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-sm text-gray-600">Preview:</span>
                        <img 
                          src={iconPreview} 
                          alt="Icon preview" 
                          className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                          data-testid="icon-preview"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="position">Widget Position</Label>
                    <select
                      id="position"
                      data-testid="position-select"
                      value={widgetSettings.position}
                      onChange={(e) => setWidgetSettings({ ...widgetSettings, position: e.target.value })}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="bottom-right">Bottom Right</option>
                      <option value="bottom-left">Bottom Left</option>
                      <option value="top-right">Top Right</option>
                      <option value="top-left">Top Left</option>
                    </select>
                  </div>

                  {/* Auto-open Widget - Desktop */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <Label htmlFor="auto-open-desktop">Auto-open on Desktop</Label>
                      <p className="text-xs text-gray-500">Open the chat widget automatically on desktop devices</p>
                    </div>
                    <input
                      type="checkbox"
                      id="auto-open-desktop"
                      checked={widgetSettings.autoOpenDesktop || false}
                      onChange={(e) => setWidgetSettings({ ...widgetSettings, autoOpenDesktop: e.target.checked })}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </div>

                  {/* Auto-open Widget - Mobile */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <Label htmlFor="auto-open-mobile">Auto-open on Mobile</Label>
                      <p className="text-xs text-gray-500">Open the chat widget automatically on mobile devices</p>
                    </div>
                    <input
                      type="checkbox"
                      id="auto-open-mobile"
                      checked={widgetSettings.autoOpenMobile || false}
                      onChange={(e) => setWidgetSettings({ ...widgetSettings, autoOpenMobile: e.target.checked })}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </div>

                  {/* Greeting Message */}
                  <div className="space-y-2">
                    <Label htmlFor="greeting-message">Chatbot Greeting Message</Label>
                    <p className="text-xs text-gray-500 mb-2">The message displayed when users first open the chat widget</p>
                    <textarea
                      id="greeting-message"
                      value={widgetSettings.greetingMessage || `Welcome to ${client?.name || 'our service'}! How can we assist you today?`}
                      onChange={(e) => setWidgetSettings({ ...widgetSettings, greetingMessage: e.target.value })}
                      className="w-full p-3 border rounded-md resize-none"
                      rows={2}
                      placeholder="Enter greeting message..."
                    />
                  </div>

                  {/* Chatbot Name */}
                  <div className="space-y-2">
                    <Label htmlFor="chatbot-name-design">Chatbot Name</Label>
                    <Input
                      id="chatbot-name-design"
                      type="text"
                      value={chatbotName}
                      onChange={(e) => setChatbotName(e.target.value)}
                      placeholder="My Support Bot"
                    />
                    <p className="text-sm text-gray-500">This name will be displayed in the chat widget header</p>
                  </div>

                  {/* Disclaimer Footer */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label htmlFor="show-disclaimer">Show Disclaimer Footer</Label>
                        <p className="text-xs text-gray-500">Display an AI disclaimer at the bottom of the chat</p>
                      </div>
                      <input
                        type="checkbox"
                        id="show-disclaimer"
                        checked={widgetSettings.showDisclaimer || false}
                        onChange={(e) => setWidgetSettings({ ...widgetSettings, showDisclaimer: e.target.checked })}
                        className="w-4 h-4 cursor-pointer"
                      />
                    </div>
                    {widgetSettings.showDisclaimer && (
                      <div className="space-y-2 mt-3">
                        <Label htmlFor="disclaimer-text">Disclaimer Text</Label>
                        <textarea
                          id="disclaimer-text"
                          value={widgetSettings.disclaimerText || "This is an AI-powered assistant. Responses are automated and may not always be accurate or complete. For definitive information, please contact support."}
                          onChange={(e) => setWidgetSettings({ ...widgetSettings, disclaimerText: e.target.value })}
                          className="w-full p-2 border rounded-md resize-none text-xs"
                          rows={3}
                          placeholder="Enter disclaimer text..."
                        />
                      </div>
                    )}
                  </div>
                </div>

                <Button onClick={handleSaveDesign} className="w-full" data-testid="save-design-button">
                  Save Design Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Form Tab */}
          <TabsContent value="form" className="space-y-4">
            <Card className="glass">
              <CardHeader>
                <CardTitle>Contact Form Settings</CardTitle>
                <CardDescription>Configure when and how visitors can contact you</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Form */}
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Enable Contact Form</Label>
                    <p className="text-sm text-gray-600 mt-1">Allow visitors to send you messages</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formSettings.enabled}
                    onChange={(e) => setFormSettings({ ...formSettings, enabled: e.target.checked })}
                    className="w-5 h-5 cursor-pointer"
                  />
                </div>

                {formSettings.enabled && (
                  <>
                    {/* Notification Email */}
                    <div className="space-y-2">
                      <Label htmlFor="notification-email">Business Email for Notifications *</Label>
                      <Input
                        id="notification-email"
                        type="email"
                        placeholder="your@email.com"
                        value={formSettings.notification_email}
                        onChange={(e) => setFormSettings({ ...formSettings, notification_email: e.target.value })}
                      />
                      <p className="text-sm text-gray-500">Receive contact form submissions and conversation notifications at this address</p>
                    </div>

                    {/* Trigger Phrases */}
                    <div className="space-y-2">
                      <Label>Trigger Phrases</Label>
                      <p className="text-sm text-gray-600 mb-2">
                        Bot will suggest the contact form when users mention these phrases
                      </p>
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="e.g., appointment, refund, speak to someone"
                          value={newTriggerPhrase}
                          onChange={(e) => setNewTriggerPhrase(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && newTriggerPhrase.trim()) {
                              setFormSettings({
                                ...formSettings,
                                trigger_phrases: [...formSettings.trigger_phrases, newTriggerPhrase.trim()]
                              });
                              setNewTriggerPhrase('');
                            }
                          }}
                        />
                        <Button
                          onClick={() => {
                            if (newTriggerPhrase.trim()) {
                              setFormSettings({
                                ...formSettings,
                                trigger_phrases: [...formSettings.trigger_phrases, newTriggerPhrase.trim()]
                              });
                              setNewTriggerPhrase('');
                            }
                          }}
                        >
                          Add
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formSettings.trigger_phrases.map((phrase, idx) => (
                          <div
                            key={idx}
                            className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                          >
                            {phrase}
                            <button
                              onClick={() => {
                                setFormSettings({
                                  ...formSettings,
                                  trigger_phrases: formSettings.trigger_phrases.filter((_, i) => i !== idx)
                                });
                              }}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-2">
                      <Label>Form Fields</Label>
                      <p className="text-sm text-gray-600 mb-2">Select which fields to collect</p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="field-name"
                            checked={formSettings.form_fields.name}
                            onChange={(e) => setFormSettings({
                              ...formSettings,
                              form_fields: { ...formSettings.form_fields, name: e.target.checked }
                            })}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="field-name" className="cursor-pointer">Name (Required)</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="field-email"
                            checked={formSettings.form_fields.email}
                            onChange={(e) => setFormSettings({
                              ...formSettings,
                              form_fields: { ...formSettings.form_fields, email: e.target.checked }
                            })}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="field-email" className="cursor-pointer">Email (Required)</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="field-phone"
                            checked={formSettings.form_fields.phone}
                            onChange={(e) => setFormSettings({
                              ...formSettings,
                              form_fields: { ...formSettings.form_fields, phone: e.target.checked }
                            })}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="field-phone" className="cursor-pointer">Phone (Optional)</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="field-message"
                            checked={formSettings.form_fields.message}
                            onChange={(e) => setFormSettings({
                              ...formSettings,
                              form_fields: { ...formSettings.form_fields, message: e.target.checked }
                            })}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="field-message" className="cursor-pointer">Message (Required)</Label>
                        </div>
                      </div>
                    </div>

                    {/* Customization */}
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="form-title">Form Title</Label>
                        <Input
                          id="form-title"
                          value={formSettings.form_title}
                          onChange={(e) => setFormSettings({ ...formSettings, form_title: e.target.value })}
                          placeholder="Contact Us"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="form-description">Form Description</Label>
                        <Input
                          id="form-description"
                          value={formSettings.form_description}
                          onChange={(e) => setFormSettings({ ...formSettings, form_description: e.target.value })}
                          placeholder="Fill out the form below..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="success-message">Success Message</Label>
                        <Input
                          id="success-message"
                          value={formSettings.success_message}
                          onChange={(e) => setFormSettings({ ...formSettings, success_message: e.target.value })}
                          placeholder="Thank you! We'll be in touch shortly."
                        />
                      </div>
                    </div>

                    <Button onClick={handleSaveFormSettings} className="w-full">
                      Save Form Settings
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Leads/Contact Requests */}
            {formSettings.enabled && (
              <Card className="glass">
                <CardHeader>
                  <CardTitle>Contact Requests ({contactRequests.length})</CardTitle>
                  <CardDescription>Messages from your chatbot visitors</CardDescription>
                </CardHeader>
                <CardContent>
                  {contactRequests.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No contact requests yet</p>
                      <p className="text-sm mt-2">Requests will appear here when visitors submit the contact form</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {contactRequests.map((request) => (
                        <div key={request.id} className="border rounded-lg p-4 space-y-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{request.name}</p>
                              <p className="text-sm text-gray-600">{request.email}</p>
                              {request.phone && <p className="text-sm text-gray-600">{request.phone}</p>}
                            </div>
                            <div className="flex gap-2">
                              <select
                                value={request.status}
                                onChange={async (e) => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    await axios.put(
                                      `${API}/contact-requests/${request.id}/status`,
                                      { status: e.target.value },
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    fetchContactRequests();
                                    toast.success('Status updated');
                                  } catch (error) {
                                    toast.error('Failed to update status');
                                  }
                                }}
                                className="text-sm border rounded px-2 py-1"
                              >
                                <option value="new">New</option>
                                <option value="contacted">Contacted</option>
                                <option value="resolved">Resolved</option>
                              </select>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={async () => {
                                  if (!window.confirm('Delete this request?')) return;
                                  try {
                                    const token = localStorage.getItem('token');
                                    await axios.delete(`${API}/contact-requests/${request.id}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    fetchContactRequests();
                                    toast.success('Request deleted');
                                  } catch (error) {
                                    toast.error('Failed to delete');
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                          <p className="text-sm bg-gray-50 p-2 rounded">{request.message}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(request.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>


          {/* Preview Tab */}
          <TabsContent value="preview" className="space-y-4">
            <Card className="glass">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Test Your Chatbot</CardTitle>
                    <CardDescription>Try your chatbot before embedding it on your website</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => window.open(`/preview/${id}`, '_blank', 'width=1200,height=800')}
                    className="flex items-center gap-2"
                  >
                    <Globe className="w-4 h-4" />
                    Open Standalone Preview
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4" style={{ height: '650px', position: 'relative' }}>
                  <iframe
                    src={`/widget/${id}`}
                    data-testid="widget-preview-iframe"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: '8px'
                    }}
                    title="Chat Widget Preview"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-3 text-center">
                  This is how your chatbot will appear on your website
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Conversations Tab */}
          <TabsContent value="conversations" className="space-y-4">
            <Card className="glass">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Conversation History</CardTitle>
                    <CardDescription>View all customer conversations</CardDescription>
                  </div>
                  <div className="flex gap-2 items-center">
                    {/* Bulk Delete Button */}
                    {selectedConversationIds.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleBulkDeleteConversations}
                        data-testid="bulk-delete-button"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete ({selectedConversationIds.length})
                      </Button>
                    )}
                    {/* Filter Toggle */}
                    <Button 
                      variant={conversationFilter === 'active' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setConversationFilter('active')}
                      data-testid="active-conversations-filter"
                    >
                      Active
                    </Button>
                    <Button 
                      variant={conversationFilter === 'archived' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setConversationFilter('archived')}
                      data-testid="archived-conversations-filter"
                    >
                      <Archive className="w-4 h-4 mr-1" />
                      Archived
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {conversations.filter(conv => {
                  if (conversationFilter === 'archived') {
                    return conv.archived === true;
                  }
                  return !conv.archived;
                }).length === 0 ? (
                  <p className="text-gray-500 text-center py-8" data-testid="no-conversations-message">
                    {conversationFilter === 'archived' ? 'No archived conversations' : 'No conversations yet'}
                  </p>
                ) : (
                  <>
                    {/* Select All Checkbox */}
                    <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 cursor-pointer"
                        data-testid="select-all-checkbox"
                      />
                      <label className="text-sm font-medium cursor-pointer" onClick={handleSelectAll}>
                        Select All ({conversations.filter(conv => {
                          if (conversationFilter === 'archived') return conv.archived === true;
                          return !conv.archived;
                        }).length})
                      </label>
                    </div>
                    
                    <div className="space-y-3">
                      {conversations.filter(conv => {
                        if (conversationFilter === 'archived') {
                          return conv.archived === true;
                        }
                        return !conv.archived;
                      }).map((conv) => (
                        <Card 
                          key={conv.id} 
                          className="p-4 hover:shadow-md transition-shadow" 
                          data-testid={`conversation-${conv.id}`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Checkbox */}
                            <input
                              type="checkbox"
                              checked={selectedConversationIds.includes(conv.id)}
                              onChange={() => handleSelectConversation(conv.id)}
                              className="w-4 h-4 mt-1 cursor-pointer flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`checkbox-${conv.id}`}
                            />
                            
                            <div className="flex-1">
                              <div className="flex justify-between items-start mb-2">
                                <div 
                                  className="flex-1 cursor-pointer"
                                  onClick={() => {
                                    setSelectedConversation(conv);
                                    setDialogOpen({ ...dialogOpen, conversation: true });
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    <MessageSquare className="w-4 h-4 text-blue-500" />
                                    <span className="font-medium">{conv.messages.length} messages</span>
                                  </div>
                                  <span className="text-sm text-gray-500">
                                    {new Date(conv.started_at).toLocaleString()}
                                  </span>
                                </div>
                                <div className="flex gap-1">
                                  {conversationFilter === 'active' ? (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleArchiveConversation(conv.id);
                                      }}
                                      data-testid={`archive-conversation-${conv.id}`}
                                      title="Archive"
                                    >
                                      <Archive className="w-4 h-4 text-gray-500" />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUnarchiveConversation(conv.id);
                                      }}
                                      data-testid={`unarchive-conversation-${conv.id}`}
                                      title="Restore"
                                    >
                                      <ArchiveRestore className="w-4 h-4 text-blue-500" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteConversation(conv.id);
                                    }}
                                    data-testid={`delete-conversation-${conv.id}`}
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                              <div 
                                className="text-sm text-gray-600 cursor-pointer"
                                onClick={() => {
                                  setSelectedConversation(conv);
                                  setDialogOpen({ ...dialogOpen, conversation: true });
                                }}
                              >
                                Last: {getMessagePreview(conv.messages[conv.messages.length - 1]?.content).substring(0, 100)}...
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex justify-center items-center gap-2 mt-6">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchConversations(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </Button>
                        
                        {/* Page Numbers */}
                        <div className="flex gap-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                            // Show first page, last page, current page, and pages around current
                            if (
                              pageNum === 1 ||
                              pageNum === totalPages ||
                              (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                            ) {
                              return (
                                <Button
                                  key={pageNum}
                                  variant={currentPage === pageNum ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => fetchConversations(pageNum)}
                                  className="min-w-[40px]"
                                >
                                  {pageNum}
                                </Button>
                              );
                            } else if (
                              pageNum === currentPage - 2 ||
                              pageNum === currentPage + 2
                            ) {
                              return <span key={pageNum} className="px-2">...</span>;
                            }
                            return null;
                          })}
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchConversations(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Conversation Detail Modal */}
            <Dialog open={dialogOpen.conversation} onOpenChange={(open) => setDialogOpen({ ...dialogOpen, conversation: open })}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden" data-testid="conversation-detail-modal">
                <DialogHeader>
                  <DialogTitle>Conversation Details</DialogTitle>
                  <DialogDescription>
                    Started: {selectedConversation && new Date(selectedConversation.started_at).toLocaleString()}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 overflow-y-auto max-h-[60vh]">
                  {/* Scrollable Messages */}
                  <div className="border rounded-lg p-4 space-y-3" data-testid="conversation-messages">
                    {selectedConversation?.messages.map((msg, index) => {
                      // Parse JSON for assistant messages
                      let structuredData = null;
                      if (msg.role === 'assistant') {
                        try {
                          structuredData = JSON.parse(msg.content);
                        } catch (e) {
                          // If parsing fails, treat as regular text
                          structuredData = null;
                        }
                      }
                      
                      return (
                        <div
                          key={index}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] p-3 rounded-lg ${
                              msg.role === 'user'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100'
                            }`}
                          >
                            <div className="text-xs mb-1 opacity-70">
                              {msg.role === 'user' ? 'Customer' : 'AI Assistant'}
                            </div>
                            
                            {/* Render content based on whether it's structured JSON or plain text */}
                            {structuredData ? (
                              <div className="text-sm">
                                {structuredData.tone_intro && (
                                  <div className="mb-2 text-gray-700 italic" dangerouslySetInnerHTML={{ __html: structuredData.tone_intro }} />
                                )}
                                <div className="mb-2 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: structuredData.answer }} />
                                
                                {structuredData.products && structuredData.products.length > 0 && (
                                  <div className="mt-2 space-y-2">
                                    {structuredData.products.map((product, idx) => (
                                      <div key={idx} className="border-l-2 border-blue-500 pl-2 py-1">
                                        <a 
                                          href={product.url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="font-semibold text-blue-600 hover:underline"
                                        >
                                          {product.name}
                                        </a>
                                        {product.price && (
                                          <span className="ml-2 text-green-600 font-medium">{product.price}</span>
                                        )}
                                        <p className="text-xs text-gray-600 mt-1">{product.why_relevant}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {structuredData.resources && structuredData.resources.length > 0 && (
                                  <div className="mt-2 space-y-2">
                                    {structuredData.resources.map((resource, idx) => (
                                      <div key={idx} className="border-l-2 border-purple-500 pl-2 py-1">
                                        <a 
                                          href={resource.url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="font-semibold text-purple-600 hover:underline"
                                        >
                                          {resource.name}
                                        </a>
                                        <p className="text-xs text-gray-600 mt-1">{resource.description}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {structuredData.citations && structuredData.citations.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {structuredData.citations.map((citation, idx) => (
                                      <div key={idx} className="text-xs">
                                        <a 
                                          href={citation} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:underline break-all"
                                        >
                                          🔗 {citation}
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {structuredData.follow_up && (
                                  <div className="mt-2 text-gray-700 italic text-xs" dangerouslySetInnerHTML={{ __html: structuredData.follow_up }} />
                                )}
                              </div>
                            ) : (
                              <p className="text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere">{msg.content}</p>
                            )}
                            
                            <div className="text-xs mt-1 opacity-70">
                              {new Date(msg.timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Email Section */}
                  <div className="border-t pt-4">
                    <Label htmlFor="email-conversation">Email Transcript</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="email-conversation"
                        type="email"
                        placeholder="customer@example.com"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        data-testid="email-input"
                      />
                      <Button 
                        onClick={handleEmailConversation} 
                        disabled={sendingEmail || !emailAddress}
                        data-testid="send-email-button"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        {sendingEmail ? 'Sending...' : 'Send'}
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Embed Code Tab */}
          <TabsContent value="embed" className="space-y-4">
            <Card className="glass">
              <CardHeader>
                <CardTitle>Embed Your Chatbot</CardTitle>
                <CardDescription>Copy and paste this code before the closing &lt;/body&gt; tag</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                  <pre className="text-sm" data-testid="embed-code">{widgetCode}</pre>
                </div>
                <Button
                  onClick={handleCopyCode}
                  className="w-full mt-4"
                  data-testid="copy-embed-code-button"
                >
                  <Code className="w-4 h-4 mr-2" /> Copy Code
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card className="glass">
              <CardHeader>
                <CardTitle>Settings</CardTitle>
                <CardDescription>Manage your account and chatbot configuration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* User Account Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Account Settings</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company-name">Company Name</Label>
                      <Input
                        id="company-name"
                        type="text"
                        value={userProfile.company_name}
                        onChange={(e) => setUserProfile({ ...userProfile, company_name: e.target.value })}
                        placeholder="Your Company Name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="user-email">Email Address</Label>
                      <Input
                        id="user-email"
                        type="email"
                        value={userProfile.email}
                        onChange={(e) => setUserProfile({ ...userProfile, email: e.target.value })}
                        placeholder="you@company.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="first-name">First Name</Label>
                      <Input
                        id="first-name"
                        type="text"
                        value={userProfile.first_name}
                        onChange={(e) => setUserProfile({ ...userProfile, first_name: e.target.value })}
                        placeholder="John"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="last-name">Last Name</Label>
                      <Input
                        id="last-name"
                        type="text"
                        value={userProfile.last_name}
                        onChange={(e) => setUserProfile({ ...userProfile, last_name: e.target.value })}
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="text-sm font-semibold text-gray-700">Change Password (Optional)</h4>
                    <p className="text-sm text-gray-500">Leave these fields empty if you don't want to change your password</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="current-password">Current Password</Label>
                        <Input
                          id="current-password"
                          type="password"
                          value={userProfile.current_password}
                          onChange={(e) => setUserProfile({ ...userProfile, current_password: e.target.value })}
                          placeholder="Enter current password"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="new-password">New Password</Label>
                        <Input
                          id="new-password"
                          type="password"
                          value={userProfile.new_password}
                          onChange={(e) => setUserProfile({ ...userProfile, new_password: e.target.value })}
                          placeholder="Enter new password"
                        />
                        <p className="text-xs text-gray-500">Min. 6 characters</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirm-password">Confirm New Password</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={userProfile.confirm_password}
                          onChange={(e) => setUserProfile({ ...userProfile, confirm_password: e.target.value })}
                          placeholder="Confirm new password"
                        />
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleSaveProfileSettings} className="w-full md:w-auto">
                    <User className="w-4 h-4 mr-2" />
                    Save Profile
                  </Button>
                </div>

                {/* Chatbot Email Notifications Section */}
                <div className="space-y-4 pt-6 border-t">
                  <h3 className="text-lg font-semibold border-b pb-2">Email Notifications</h3>
                  <p className="text-sm text-gray-600">Configure email notifications for this chatbot</p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="client-send-recap"
                        checked={clientEmailSettings?.recap_enabled || false}
                        onChange={(e) => setClientEmailSettings({ ...clientEmailSettings, recap_enabled: e.target.checked })}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        data-testid="client-recap-checkbox"
                      />
                      <Label htmlFor="client-send-recap" className="cursor-pointer">
                        Send conversation recap emails
                      </Label>
                    </div>
                    <p className="text-sm text-gray-500 ml-6">
                      Receive a summary of each conversation for this chatbot when it ends
                    </p>
                    
                    <div className="space-y-2">
                      <Label htmlFor="client-recap-email">Email for Conversation Recaps</Label>
                      <Input
                        id="client-recap-email"
                        type="email"
                        placeholder="notifications@company.com"
                        value={clientEmailSettings?.recap_email || ''}
                        onChange={(e) => setClientEmailSettings({ ...clientEmailSettings, recap_email: e.target.value })}
                        disabled={!clientEmailSettings?.recap_enabled}
                        className={!clientEmailSettings?.recap_enabled ? 'bg-gray-100' : ''}
                        data-testid="client-recap-email-input"
                      />
                      <p className="text-sm text-gray-500">
                        Conversation summaries for this chatbot will be sent here
                      </p>
                    </div>

                    <Button onClick={handleSaveEmailSettings} className="w-full md:w-auto">
                      <Mail className="w-4 h-4 mr-2" />
                      Save Email Settings
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
